import React, { useEffect, useState } from 'react';
import { api, resolveImg } from '../../lib/api';
import ImageUpload from '../../components/admin/ImageUpload';
import { Loader2, Plus, Pencil, Trash2, X, ShieldCheck } from 'lucide-react';

const empty = { name: '', abbr: '', detail: '', image: '', order: 99 };

const CertificatesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/certificates'); setItems(data); } catch (e) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, order: items.length + 1 }); setEditing('new'); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditing(c.id); };
  const close = () => { setEditing(null); setForm(empty); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.abbr.trim()) { alert('Name and short code are required'); return; }
    const payload = { ...form, order: Number(form.order) || 0 };
    setSaving(true);
    try {
      if (editing === 'new') await api.post('/admin/certificates', payload);
      else await api.put(`/admin/certificates/${editing}`, payload);
      close(); await load();
    } catch (e) { alert('Save failed'); }
    setSaving(false);
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this certificate?')) return;
    try { await api.delete(`/admin/certificates/${id}`); await load(); } catch (e) {}
  };

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-ktc-green" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-ktc-charcoal/60">{items.length} certificate(s)</p>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory text-sm font-medium px-4 py-2.5 rounded-md transition-colors"><Plus size={16} /> New Certificate</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((c) => (
          <div key={c.id} className="bg-ktc-ivoryLight border border-ktc-cream rounded-xl overflow-hidden">
            <div className="h-40 bg-ktc-cream/40 grid place-items-center">{c.image ? <img src={resolveImg(c.image)} alt={c.name} className="w-full h-full object-contain p-2" /> : <ShieldCheck size={36} className="text-ktc-gold" />}</div>
            <div className="p-4">
              <span className="font-serif text-xl font-bold text-ktc-gold">{c.abbr}</span>
              <h3 className="font-serif text-lg font-semibold text-ktc-green leading-snug">{c.name}</h3>
              <p className="text-xs text-ktc-charcoal/60 mt-1 line-clamp-2">{c.detail}</p>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => openEdit(c)} className="inline-flex items-center gap-1.5 text-sm text-ktc-green font-medium"><Pencil size={14} /> Edit</button>
                <button onClick={() => remove(c.id)} className="ml-auto inline-flex items-center gap-1.5 text-sm text-red-600"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] bg-ktc-greenDark/90 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
          <div className="bg-ktc-ivoryLight rounded-2xl max-w-xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-ktc-cream">
              <h2 className="font-serif text-2xl font-semibold text-ktc-green">{editing === 'new' ? 'New Certificate' : 'Edit Certificate'}</h2>
              <button onClick={close} className="text-ktc-charcoal/50 hover:text-ktc-green"><X size={22} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <label className="block sm:col-span-2"><span className="text-sm font-medium text-ktc-charcoal/80">Name *</span><input className="ktc-input mt-1.5" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. ISO 9001:2015" /></label>
                <label className="block"><span className="text-sm font-medium text-ktc-charcoal/80">Short Code *</span><input className="ktc-input mt-1.5" value={form.abbr} onChange={(e) => set('abbr', e.target.value)} placeholder="e.g. ISO" /></label>
              </div>
              <label className="block"><span className="text-sm font-medium text-ktc-charcoal/80">Detail</span><input className="ktc-input mt-1.5" value={form.detail} onChange={(e) => set('detail', e.target.value)} /></label>
              <label className="block w-32"><span className="text-sm font-medium text-ktc-charcoal/80">Display Order</span><input type="number" className="ktc-input mt-1.5" value={form.order} onChange={(e) => set('order', e.target.value)} /></label>
              <ImageUpload value={form.image} onChange={(v) => set('image', v)} label="Certificate Scan" />
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-ktc-cream">
              <button onClick={close} className="px-5 py-2.5 rounded-md border border-ktc-cream text-ktc-charcoal/70 text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory text-sm font-medium px-6 py-2.5 rounded-md transition-colors">{saving && <Loader2 size={15} className="animate-spin" />} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificatesPanel;
